import React, { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { StockMenu, WatchAdd } from "@/components/WatchAdd";
import { copySymbols } from "@/pages/Scanner";
import { Copy } from "lucide-react";
import { useFlow, FlowTags, FlowNote } from "@/components/FlowMarks";

const Row = ({ r, hot, m }) => (
  <div className="flex items-center gap-1">
    <StockMenu instrumentId={r.instrument_id} symbol={r.symbol}>
      <button className="flex-1 min-w-0 flex items-center justify-between gap-2 text-xs font-mono px-1.5 py-1 hover:bg-blue-500/10 transition-colors text-left" data-testid={`tfradr-row-${r.symbol}`}>
        <span className="w-40 shrink-0 leading-tight text-slate-900 font-semibold">{r.symbol}
          <FlowTags m={m} testid={`tfradr-${r.symbol}`} />
          {hot?.top?.includes(r.sector) && <span className="ml-1 text-[8px] px-1 font-bold bg-emerald-600 text-white">TOP</span>}
          {hot?.bottom?.includes(r.sector) && <span className="ml-1 text-[8px] px-1 font-bold bg-rose-600 text-white">WORST</span>}
          {r.big > 0 && <span className="ml-1 text-[9px] px-1 font-bold bg-amber-400 text-black">BIG {r.big}×</span>}
          <span className="block text-[9px] text-slate-500 font-normal">{r.sector}</span></span>
        <span className="text-[10px] text-violet-300 leading-tight" data-testid={`tfradr-lines-${r.symbol}`}>
          {r.lines.map((x) => <span key={x.line} className="block whitespace-nowrap">{x.line} {inr(x.poc)} · {x.cross_at}</span>)}</span>
        <span className="text-[10px] text-sky-300 leading-tight whitespace-nowrap" data-testid={`tfradr-adr-${r.symbol}`}>RM {inr(r.adr_level)}<span className="block">{r.adr_at}</span></span>
        <span className="text-right leading-tight"><span className="text-slate-600 block">{inr(r.ltp)}</span>
          <span className={r.change_pct >= 0 ? "text-emerald-600" : "text-rose-600"}>{r.change_pct > 0 ? "+" : ""}{r.change_pct}%</span></span>
      </button>
    </StockMenu>
    <WatchAdd instrumentId={r.instrument_id} symbol={r.symbol} />
  </div>
);

export const TfrAdrPanel = ({ hot }) => {
  const [d, setD] = useState(null);
  const [dir, setDir] = useState("up");
  useEffect(() => {
    const load = () => api.get("/today/poc-cross").then((r) => setD(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  const flow = useFlow();
  if (!d) return <p className="text-xs font-mono text-slate-500">Loading…</p>;
  const all = flow.filter(d.adr_x || []);
  const rows = all.filter((r) => r.dir === dir);
  return (
    <div data-testid="tfradr-panel">
      <div className="grid grid-cols-2 gap-1 mb-2">
        {[["up", "Bullish · above Range Matrix high", "text-emerald-600"], ["down", "Bearish · below Range Matrix low", "text-rose-600"]].map(([k, label, c]) => (
          <button key={k} onClick={() => setDir(k)} data-testid={`tfradr-tab-${k}`}
            className={`text-[10px] font-mono font-bold px-1 py-1 border transition-colors ${dir === k ? `border-blue-500 ${c} bg-blue-500/15` : "border-slate-200 text-slate-500"}`}>
            {label} <span className="text-slate-500" data-testid={`tfradr-count-${k}`}>{all.filter((r) => r.dir === k).length}</span>
          </button>
        ))}
      </div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-[9px] font-mono text-slate-500">crossed TFR D (5m) and broke the Range Matrix band the same way · any order · latest first</span>
        {rows.length > 0 && <button onClick={() => copySymbols(rows.map((r) => r.symbol), "symbols")} data-testid="tfradr-copy"
          className="text-[10px] font-mono text-blue-600 flex items-center gap-1"><Copy className="h-3 w-3" />copy</button>}
      </div>
      <FlowNote />
      {!rows.length && <p className="text-xs font-mono text-slate-500 py-3" data-testid="tfradr-empty">No stocks yet today</p>}
      <div className="space-y-px max-h-72 overflow-y-auto">{rows.map((r) => <Row key={r.symbol} r={r} hot={hot} m={flow.marks[r.symbol]} />)}</div>
    </div>
  );
};
